import pluralize from 'pluralize';

import { Icons, StatusChipV2 } from '@cast/design-system';

import { useReportingTrialExplainDialog } from './dialogs/useReportingTrialExplainDialog';
import type { ReportingTrialFleet } from './reportingPlans';
import { useReportingTrial } from './useReportingTrial';
import type { ReportingTrial } from './useReportingTrial';

const TrialStatusChip = ({
  text,
  tone = 'success',
  onClick,
}: {
  text: string;
  tone?: 'success' | 'danger';
  onClick?: () => void;
}) => (
  <StatusChipV2
    label={text}
    color={tone === 'danger' ? 'error' : 'success'}
    variant="subtle"
    size="large"
    testId="reporting-trial-status-badge"
    slots={{ icon: <Icons.Clock weight="bold" /> }}
    sx={{ cursor: 'pointer', border: 'none' }}
    {...{
      component: 'button',
      type: 'button',
      onClick,
    }}
  />
);

/** Navbar Status chip: reporting trial phases only (never when paid). */
export const shouldShowReportingTrialStatus = (
  trial: Pick<ReportingTrial, 'isEligible' | 'state'>
): boolean => trial.isEligible && trial.state !== 'paid';

type ReportingTrialStatusBadgeProps = {
  onViewPlans?: () => void;
  onContactSales?: () => void;
  fleet?: ReportingTrialFleet;
};

const daysLeftText = (days: number, prefix: string): string =>
  days === 0
    ? `${prefix} today`
    : `${prefix} in ${days} ${pluralize('day', days)}`;

export const ReportingTrialStatusBadge = ({
  onViewPlans,
  onContactSales,
  fleet,
}: ReportingTrialStatusBadgeProps) => {
  const trial = useReportingTrial();
  const { openDialog } = useReportingTrialExplainDialog();

  if (!shouldShowReportingTrialStatus(trial)) {
    return null;
  }

  const { state, isAboutToExpire, expiresInDays, expiresInDaysGracePeriod } =
    trial;

  const handleClick = () => {
    openDialog({ onViewPlans, onContactSales, fleet });
  };

  if (state === 'post-trial') {
    return (
      <TrialStatusChip
        text="Trial expired"
        tone="danger"
        onClick={handleClick}
      />
    );
  }

  if (state === 'grace-period') {
    return (
      <TrialStatusChip
        text={daysLeftText(expiresInDaysGracePeriod ?? 0, 'Grace period ends')}
        tone="danger"
        onClick={handleClick}
      />
    );
  }

  if (state === 'active') {
    if (isAboutToExpire) {
      return (
        <TrialStatusChip
          text={daysLeftText(expiresInDays, 'Trial expires')}
          tone="danger"
          onClick={handleClick}
        />
      );
    }

    // Missing trialExpiresAt yields a non-finite countdown — render
    // neutral copy instead of "Trial: Infinity days left".
    if (!Number.isFinite(expiresInDays)) {
      return <TrialStatusChip text="Trial" onClick={handleClick} />;
    }

    return (
      <TrialStatusChip
        text={`Trial: ${expiresInDays} ${pluralize('day', expiresInDays)} left`}
        onClick={handleClick}
      />
    );
  }

  return null;
};
